"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BaseRewardImplementation = exports.RewardFormat = void 0;
exports.loadRewardImplementations = loadRewardImplementations;
const assert_1 = __importDefault(require("assert"));
const lodash_1 = __importDefault(require("lodash"));
const assertUtils_1 = require("../../assertUtils");
const depositProductNames_1 = require("../../deposit/depositProductNames");
const onboarding_1 = require("../../deposit/onboarding");
const my_1 = require("../../my");
const rewards_1 = require("../../rewards");
const utils_1 = require("../../utils");
const assertUtil_1 = require("../../utils/assertUtil");
const nameFormat_1 = require("../../utils/nameFormat");
const numberUtil_1 = require("../../utils/numberUtil");
const stringUtil_1 = require("../../utils/stringUtil");
const rewardAppliedWhereRule_1 = require("../rewardRules/rewardAppliedWhereRule");
const rewardConditionRule_1 = require("../rewardRules/rewardConditionRule");
const rewardLimitRule_1 = require("../rewardRules/rewardLimitRule");
const rewardDefinitions_1 = require("./rewardDefinitions");
var RewardFormat;
(function (RewardFormat) {
    RewardFormat["HUMAN_READABLE"] = "HUMAN_READABLE";
    RewardFormat["DISPLAY_NAME"] = "DISPLAY_NAME";
    RewardFormat["OFFER_COMPONENT"] = "OFFER_COMPONENT";
    RewardFormat["HUMAN_READABLE_SPECIFIC"] = "HUMAN_READABLE_SPECIFIC";
})(RewardFormat || (exports.RewardFormat = RewardFormat = {}));
const SELECTED_ANNUAL_AMOUNT_HUMAN_READABLE = 'your selected annual promotional spend amount';
class BaseRewardImplementation {
    rewardDefinition;
    constructor(rewardDefinition) {
        this.rewardDefinition = rewardDefinition;
    }
    /**
     * Returns the reward formatted according to the specified format.
     */
    getFormattedReward(format, options = {}) {
        switch (format) {
            case RewardFormat.HUMAN_READABLE:
                return this.getHumanReadableReward(options);
            case RewardFormat.DISPLAY_NAME:
                return this.getDisplayRewardName();
            case RewardFormat.OFFER_COMPONENT:
                return this.getOfferComponentReward(options);
            case RewardFormat.HUMAN_READABLE_SPECIFIC:
                return this.getHumanReadableSpecific(options);
            default:
                return (0, assertUtils_1.assertNever)(format, `Unknown reward format: ${format}`);
        }
    }
    getHumanReadableReward(options) {
        const parts = [
            this.getPercentageCashbackHumanReadable(),
            this.getLimitRulesHumanReadable(true, options),
            this.getAppliedWhereRuleHumanReadable(),
            this.getAutopayConditionsHumanReadable(),
        ].filter((part) => part);
        return parts.join(' ');
    }
    getDisplayRewardName() {
        const parts = [this.getPercentageCashbackAndUnlimitedTextForDisplayName(), this.getAppliedWhereRuleHumanReadable()].filter((part) => part);
        return (0, nameFormat_1.toTitleCase)(parts.join(' '));
    }
    getPercentageCashbackAndUnlimitedTextForDisplayName() {
        const unlimited = !this.hasLimitRules();
        const cashbackRatio = this.rewardDefinition.cashbackRatio;
        return `${(0, stringUtil_1.toPercentFormat)(cashbackRatio)} ${cashbackRatio > 0 && unlimited ? 'Unlimited ' : ''}Cash Back`;
    }
    /**
     * Thought for usage in offer components where we want to highlight the main reward and the limit rules only, for
     * example aven_frontend/aven/src/components/offerRender/OfferCardV3.vue
     */
    getOfferComponentReward(options) {
        const parts = [this.getPercentageCashbackHumanReadable(), this.getLimitRulesHumanReadable(true, options)].filter(Boolean);
        return parts.join(' ');
    }
    /**
     * Thought or usage in terms and conditions document where we want to be very specific about all the reward details
     */
    getHumanReadableSpecific(options) {
        let limitRulesAmountPart = this.getLimitRulesLimitAmount(false, options);
        if (limitRulesAmountPart && this.headlineLimitRuleHasFixedAmount()) {
            limitRulesAmountPart += ' spent';
        }
        const durationPart = [this.getLimitRulesDurationHumanReadable(), this.getAdditionalLimitRulesHumanReadable({ abbreviate: false, options })].filter(Boolean).join('');
        const parts = [
            `you will earn ${this.getPointsPerDollarSpentHumanReadable()}`,
            limitRulesAmountPart,
            this.getSpecificAppliedWhereRuleHumanReadable(),
            durationPart,
            this.getAutopayConditionsHumanReadable(),
            this.getDepositBundleConditionsHumanReadable(),
        ].filter(Boolean);
        return parts.join(' ');
    }
    getPercentageCashbackHumanReadable() {
        return `${(0, stringUtil_1.toPercentFormat)(this.rewardDefinition.cashbackRatio)} Cash Back`;
    }
    getPointsPerDollarSpentHumanReadable() {
        return `${(0, numberUtil_1.roundToNDecimalPlaces)(this.rewardDefinition.cashbackRatio * 100, 1)} Points per $1 spent`;
    }
    hasLimitRules() {
        return this.rewardDefinition.limitRules.length > 0;
    }
    isWhereUnrestricted() {
        return this.rewardDefinition.appliedWhereRule === rewardAppliedWhereRule_1.AppliedWhereRuleNames.UNRESTRICTED;
    }
    isUnconditional() {
        return (0, utils_1.typedKeys)(this.rewardDefinition.conditionRules).every((key) => !this.isDependentOnConditionType(key));
    }
    isDependentOnConditionType(conditionType) {
        return !lodash_1.default.isNil(this.rewardDefinition.conditionRules[conditionType]);
    }
    isAutopayIndependent() {
        return !this.isDependentOnConditionType(rewardConditionRule_1.ConditionTypes.autopayConditions);
    }
    isUnlimitedUnconditionalReward() {
        return this.isWhereUnrestricted() && !this.hasLimitRules() && this.isUnconditional();
    }
    /** Definition order is meaningful: the first limit rule is the headline cap, the rest are appended as a clause. */
    getLimitRuleAmountFormatter({ abbreviate = true }) {
        return abbreviate ? (amount) => (0, stringUtil_1.toFormattedUSDKMOrB)(amount, false) : (amount) => (0, stringUtil_1.toDollarFormat)(amount, { showFractionZeros: !Number.isInteger(amount) });
    }
    getLimitRuleDurationHumanReadable(limitRule) {
        switch (limitRule.rewardLimitType) {
            case rewards_1.RewardLimitType.lifetime:
                return null;
            case rewards_1.RewardLimitType.year:
                return 'each account year';
            case rewards_1.RewardLimitType.cycle:
                return 'each statement cycle';
            default:
                return (0, assertUtils_1.assertNever)(limitRule.rewardLimitType, `Unknown reward limit type: ${limitRule.rewardLimitType}`);
        }
    }
    getLimitRuleAmountHumanReadable(limitRule, abbreviate, options) {
        const fixedAmountDollars = (0, rewardLimitRule_1.getFixedLimitAmountDollars)(limitRule);
        if (!lodash_1.default.isNil(fixedAmountDollars)) {
            return this.getLimitRuleAmountFormatter({ abbreviate })(fixedAmountDollars);
        }
        switch (limitRule.rewardLimitType) {
            case rewards_1.RewardLimitType.year:
                return SELECTED_ANNUAL_AMOUNT_HUMAN_READABLE;
            case rewards_1.RewardLimitType.cycle: {
                const oneTwelfthOfSelectedAnnualAmount = `1/12 of ${SELECTED_ANNUAL_AMOUNT_HUMAN_READABLE}`;
                const minCycleLimitDollars = options.rewardsCheckingBundleMinCycleLimitDollars;
                if (lodash_1.default.isNil(minCycleLimitDollars)) {
                    return oneTwelfthOfSelectedAnnualAmount;
                }
                return `the greater of ${(0, stringUtil_1.toDollarFormat)(minCycleLimitDollars, { showFractionZeros: false })} or ${oneTwelfthOfSelectedAnnualAmount}`;
            }
            case rewards_1.RewardLimitType.lifetime:
                throw new Error(rewardLimitRule_1.LIFETIME_LIMIT_CANNOT_BE_SOURCED_FROM_ANNUAL_AMOUNT_MESSAGE);
            default:
                return (0, assertUtils_1.assertNever)(limitRule.rewardLimitType, `Unknown reward limit type: ${limitRule.rewardLimitType}`);
        }
    }
    headlineLimitRuleHasFixedAmount() {
        const headlineRule = this.rewardDefinition.limitRules[0];
        return !headlineRule || !lodash_1.default.isNil((0, rewardLimitRule_1.getFixedLimitAmountDollars)(headlineRule));
    }
    getLimitRulesAmountHumanReadable(abbreviate = true, options = {}) {
        const headlineRule = this.rewardDefinition.limitRules[0];
        if (!headlineRule) {
            return null;
        }
        return this.getLimitRuleAmountHumanReadable(headlineRule, abbreviate, options);
    }
    /** The caps beyond the headline one, as a trailing clause. Null when the reward has at most one cap. */
    getAdditionalLimitRulesHumanReadable({ abbreviate, options = {} }) {
        const additionalRules = this.rewardDefinition.limitRules.slice(1);
        if (additionalRules.length === 0) {
            return null;
        }
        const clauses = additionalRules.map((limitRule) => [`up to ${this.getLimitRuleAmountHumanReadable(limitRule, abbreviate ?? true, options)}`, this.getLimitRuleDurationHumanReadable(limitRule)].filter(Boolean).join(' '));
        return `, ${(0, stringUtil_1.joinWithAlternateTerminalSeparator)(clauses, ', ', ' and ')}`;
    }
    getLimitRulesLimitAmount(abbreviate, options = {}) {
        const limitRuleAmountHumanReadable = this.getLimitRulesAmountHumanReadable(abbreviate, options);
        if (!limitRuleAmountHumanReadable) {
            return null;
        }
        return this.headlineLimitRuleHasFixedAmount() ? `on the first ${limitRuleAmountHumanReadable}` : `up to ${limitRuleAmountHumanReadable}`;
    }
    getLimitRulesDurationHumanReadable() {
        const headlineRule = this.rewardDefinition.limitRules[0];
        if (!headlineRule) {
            return null;
        }
        return this.getLimitRuleDurationHumanReadable(headlineRule);
    }
    getLimitRulesHumanReadable(abbreviate, options = {}) {
        if (!this.hasLimitRules()) {
            return null;
        }
        const limitRulesAmountLimitHumanReadable = this.getLimitRulesLimitAmount(abbreviate, options);
        const limitRulesDurationHumanReadable = this.getLimitRulesDurationHumanReadable();
        if (!limitRulesAmountLimitHumanReadable) {
            return null;
        }
        let phraseLimitRuleDurationHumanReadable = [this.headlineLimitRuleHasFixedAmount() ? 'spent' : null, limitRulesDurationHumanReadable].filter(Boolean).join(' ');
        return [limitRulesAmountLimitHumanReadable, phraseLimitRuleDurationHumanReadable].join(' ') + (this.getAdditionalLimitRulesHumanReadable({ abbreviate, options }) ?? '');
    }
    getSpecificAppliedWhereRuleHumanReadable() {
        const ruleName = this.rewardDefinition.appliedWhereRule;
        const prefix = 'on Net Eligible Purchases';
        switch (ruleName) {
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.UNRESTRICTED:
                return prefix;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.POINT_OF_SALE_MERCHANTS:
                return `${prefix} from the following point of sale merchants: ${(0, stringUtil_1.joinWithAlternateTerminalSeparator)(rewardAppliedWhereRule_1.POINT_OF_SALE_MERCHANTS, ', ', ' and ')}`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.HOME_DEPOT:
                return `${prefix} at Home Depot`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.GROCERIES_AND_SUPERMARKETS:
                return `${prefix} at grocery stores and supermarkets (with approved merchant codes)`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.GAS_STATIONS:
                return `${prefix} at gas stations (with approved merchant codes)`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.GAS_STATIONS_AND_AUTOMATED_FUEL_DISPENSERS:
                return `${prefix} at gas stations (with approved merchant codes)`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.AUTOMATED_FUEL_DISPENSERS:
                return `${prefix} at automated fuel dispensers (with approved merchant codes)`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.RESTAURANTS:
                return `${prefix} at restaurants (with approved merchant codes)`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.GAS_AND_GROCERIES:
                return `${prefix} on gas and groceries (with approved merchant codes)`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.STARBUCKS_DUNKIN:
                return `${prefix} at Starbucks and Dunkin'`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.GAS_GROCERIES_AND_UTILITIES:
                return `${prefix} on gas, groceries, and utilities (with approved merchant codes)`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.GROCERIES_SUPERMARKETS_AND_MISC_FOOD_STORES:
                return `${prefix} at grocery stores and supermarkets (with approved merchant codes)`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.HOA_PAYMENTS:
                return `on payments to homeowners associations`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.TRAVEL:
                return `${prefix} on travel (with approved merchant codes)`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.HOME_IMPROVEMENT:
                return `${prefix} on home improvement (with approved merchant codes)`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.UTILITIES_GAS_AND_EV_CHARGING:
                return `${prefix} on utilities, gas, and EV charging (with approved merchant codes)`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.SEMPER_FI_MERCHANT:
                return `at Semper Fi merchant (with approved merchant codes)`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.TYPE_FIVE_MERCHANT:
                return `at Type Five merchant (with approved merchant codes)`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.FOREST_RIVER_MERCHANTS:
                return `at Forest River merchant (with approved merchant codes)`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.DB_MERCHANT_CODES:
                // TODO: resolve display name from CobrandPartnerId instead of hardcoding (see #99005)
                return `at Forest River merchant (with approved merchant codes)`;
            default:
                return (0, assertUtils_1.assertNever)(ruleName, `Unknown applied where rule name: ${ruleName}`);
        }
    }
    getAppliedWhereRuleHumanReadable() {
        const ruleName = this.rewardDefinition.appliedWhereRule;
        switch (ruleName) {
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.UNRESTRICTED:
                return null;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.POINT_OF_SALE_MERCHANTS:
                return `on point of sale merchants`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.HOME_DEPOT:
                return `at Home Depot`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.GROCERIES_AND_SUPERMARKETS:
                return `at grocery stores and supermarkets`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.GAS_STATIONS:
                return `at gas stations`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.GAS_STATIONS_AND_AUTOMATED_FUEL_DISPENSERS:
                return `at gas stations`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.AUTOMATED_FUEL_DISPENSERS:
                return `at automated fuel dispensers`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.RESTAURANTS:
                return `at restaurants`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.GAS_AND_GROCERIES:
                return `on gas and groceries`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.STARBUCKS_DUNKIN:
                return `at Starbucks and Dunkin'`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.GAS_GROCERIES_AND_UTILITIES:
                return `on gas, groceries, and utilities`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.GROCERIES_SUPERMARKETS_AND_MISC_FOOD_STORES:
                return `at grocery stores and supermarkets`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.HOA_PAYMENTS:
                return `on HOA payments`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.TRAVEL:
                return `on travel`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.HOME_IMPROVEMENT:
                return `on home improvement`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.UTILITIES_GAS_AND_EV_CHARGING:
                return `on utilities, gas, and EV charging`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.SEMPER_FI_MERCHANT:
                return `at Semper Fi`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.TYPE_FIVE_MERCHANT:
                return `at Type Five`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.FOREST_RIVER_MERCHANTS:
                return `at Forest River`;
            case rewardAppliedWhereRule_1.AppliedWhereRuleNames.DB_MERCHANT_CODES:
                // TODO: resolve display name from CobrandPartnerId instead of hardcoding (see #99005)
                return `at Forest River`;
            default:
                return (0, assertUtils_1.assertNever)(ruleName, `Unknown applied where rule name: ${ruleName}`);
        }
    }
    getAutopayConditionsHumanReadable() {
        if (this.isAutopayIndependent()) {
            return null;
        }
        // NOTE: If a reward has multiple autopay conditions, this needs to be updated to include all the autopay conditions
        if (this.hasTimeAfterAutopayEnabledConditionRule()) {
            const timeAfterAutopayEnabledConditionRule = this.getTimeAfterAutopayEnabledConditionRule();
            return `for the first ${timeAfterAutopayEnabledConditionRule.numDaysAfterAutopayEnabled} days after AutoPay is enabled`;
        }
        if (this.hasAutopayCategoryConditionRule()) {
            const autopayCategoryConditionRule = this.getAutopayCategoryConditionRule();
            const categoryNames = autopayCategoryConditionRule.autopayCategories.map((category) => {
                switch (category) {
                    case my_1.AutopayCategory.STATEMENT_BALANCE:
                        return `Statement Balance`;
                    case my_1.AutopayCategory.CURRENT_BALANCE:
                        return `Current Balance`;
                    case my_1.AutopayCategory.CUSTOM_AMOUNT:
                        return `Custom Amount`;
                    case my_1.AutopayCategory.MINIMUM_DUE:
                        return `Minimum Due`;
                    case my_1.AutopayCategory.BI_MONTHLY_CURRENT_BALANCE:
                        return `Bi-Monthly Current Balance`;
                    default:
                        return (0, assertUtils_1.assertNever)(category, `Unknown autopay category: ${category}`);
                }
            });
            const joinedAutopayConditions = categoryNames.length <= 2 ? categoryNames.join(' or ') : `${categoryNames.slice(0, -1).join(', ')}, or ${categoryNames[categoryNames.length - 1]}`;
            return `with AutoPay set to ${joinedAutopayConditions}`;
        }
        if (this.hasAutopayEnabledConditionRule()) {
            return `(with AutoPay on)`;
        }
        throw new Error(`Unknown autopay condition type in getAutopayConditionsHumanReadable`);
    }
    getDepositBundleConditionsHumanReadable() {
        const depositBundleCondition = this.rewardDefinition.conditionRules.depositBundleConditions;
        if (lodash_1.default.isNil(depositBundleCondition)) {
            return null;
        }
        switch (depositBundleCondition.depositBundleConditionType) {
            case rewardConditionRule_1.DepositBundleConditionRule.REWARDS_DEPOSIT_BUNDLE_ENROLLMENT_ACTIVE:
                return `and maintain your selected minimum balance in an ${(0, depositProductNames_1.getDepositProductNames)(onboarding_1.DepositOnboardingSource.REWARDS_CHECKING_BUNDLE).depositProductName} account`;
            default:
                return (0, assertUtils_1.assertNever)(depositBundleCondition.depositBundleConditionType, `Unknown deposit condition type ${depositBundleCondition.depositBundleConditionType}`);
        }
    }
    getTimeAfterAutopayEnabledConditionRule() {
        (0, assert_1.default)(this.hasTimeAfterAutopayEnabledConditionRule());
        return this.rewardDefinition.conditionRules.autopayConditions;
    }
    hasTimeAfterAutopayEnabledConditionRule() {
        return this.rewardDefinition.conditionRules.autopayConditions?.autopayConditionType === rewardConditionRule_1.AutopayConditionRule.TIME_AFTER_ENABLED;
    }
    getAutopayCategoryConditionRule() {
        (0, assert_1.default)(this.hasAutopayCategoryConditionRule());
        return this.rewardDefinition.conditionRules.autopayConditions;
    }
    hasAutopayCategoryConditionRule() {
        return this.rewardDefinition.conditionRules.autopayConditions?.autopayConditionType === rewardConditionRule_1.AutopayConditionRule.CATEGORY;
    }
    getAutopayEnabledConditionRule() {
        (0, assert_1.default)(this.hasAutopayEnabledConditionRule());
        return this.rewardDefinition.conditionRules.autopayConditions;
    }
    hasAutopayEnabledConditionRule() {
        return this.rewardDefinition.conditionRules.autopayConditions?.autopayConditionType === rewardConditionRule_1.AutopayConditionRule.ENABLED;
    }
    hasAutopayConditionRule() {
        const hasAutopayEnabledCondition = this.hasAutopayEnabledConditionRule();
        const hasAutopayCategoryCondition = this.hasAutopayCategoryConditionRule();
        const hasTimeAfterAutopayCondition = this.hasTimeAfterAutopayEnabledConditionRule();
        return hasAutopayEnabledCondition || hasAutopayCategoryCondition || hasTimeAfterAutopayCondition;
    }
    hasLifeTimeLimitRule() {
        return this.rewardDefinition.limitRules.every((rule) => rule.rewardLimitType === rewards_1.RewardLimitType.lifetime);
    }
    getLifeTimeLimitRule() {
        const lifetimeRule = this.rewardDefinition.limitRules.find((rule) => rule.rewardLimitType === rewards_1.RewardLimitType.lifetime);
        return (0, assertUtil_1.assertValue)(lifetimeRule, 'LifeTimeLimitRule not found');
    }
    getYearLimitRule() {
        const yearRule = this.rewardDefinition.limitRules.find((rule) => rule.rewardLimitType === rewards_1.RewardLimitType.year);
        return (0, assertUtil_1.assertValue)(yearRule, 'YearLimitRule not found');
    }
    hasYearLimitRule() {
        return this.rewardDefinition.limitRules.every((rule) => rule.rewardLimitType === rewards_1.RewardLimitType.year);
    }
    getCycleLimitRule() {
        const cycleRule = this.rewardDefinition.limitRules.find((rule) => rule.rewardLimitType === rewards_1.RewardLimitType.cycle);
        return (0, assertUtil_1.assertValue)(cycleRule, 'CycleLimitRule not found');
    }
    hasCycleLimitRule() {
        return this.rewardDefinition.limitRules.every((rule) => rule.rewardLimitType === rewards_1.RewardLimitType.cycle);
    }
}
exports.BaseRewardImplementation = BaseRewardImplementation;
function loadRewardImplementations(Class = BaseRewardImplementation) {
    const result = {};
    for (const [rewardName, rewardDefinition] of Object.entries(rewardDefinitions_1.rewardDefinitions)) {
        result[rewardName] = new Class(rewardDefinition);
    }
    return result;
}
